import React, { useEffect } from 'react';
import { marked } from 'marked';
import Link from 'next/link';
import { useTranslations } from '../../locales/useTranslations';
import { BiX, BiMap, BiMoney, BiGroup, BiLinkExternal, BiEnvelope } from 'react-icons/bi';

const JobDetailModal = ({ job, translation, isOpen, onClose }) => {
    const { t } = useTranslations();

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Close on Escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            window.addEventListener('keydown', handleEscape);
        }
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen || !job || !translation) return null;

    const title = translation.title || 'Job';
    const content = translation.content || '';
    const htmlContent = marked.parse(content);
    const freePlätze = parseInt(job.anzahl) || 0;
    const hasArbeitsagenturLink = freePlätze > 0 && job.arbeitsagentur_link;
    const canApply = freePlätze > 0;

    // Format location
    const formatLocation = (arbeitsort) => {
        const locationMap = {
            'remote': 'Remote',
            'hybrid': 'Hybrid (Kiel/Remote)',
            'onsite': 'Kiel',
            'kiel': 'Kiel'
        };
        return locationMap[arbeitsort?.toLowerCase()] || arbeitsort || 'Kiel';
    };

    // Generate email subject
    const emailSubject = encodeURIComponent(`Bewerbung: ${title}`);

    return (
        <div className="job-modal-overlay" onClick={onClose}>
            <div className="job-modal" onClick={(e) => e.stopPropagation()}>
                <button className="job-modal__close" onClick={onClose} aria-label="Schließen">
                    <BiX />
                </button>

                <div className="job-modal__header">
                    <h2 className="job-modal__title">{title}</h2>
                    <div className="job-modal__meta">
                        <span className="job-modal__meta-item">
                            <BiMap /> {formatLocation(job.arbeitsort)}
                        </span>
                        {job.lohn_std && (
                            <span className="job-modal__meta-item">
                                <BiMoney /> {job.lohn_std}€/Std.
                            </span>
                        )}
                        <span className={`job-modal__meta-item ${freePlätze > 0 ? 'job-modal__meta-item--available' : 'job-modal__meta-item--unavailable'}`}>
                            <BiGroup /> {freePlätze} {freePlätze === 1 ? 'Platz' : 'Plätze'} verfügbar
                        </span>
                    </div>
                </div>

                <div className="job-modal__content">
                    <div 
                        className="job-modal__markdown"
                        dangerouslySetInnerHTML={{ __html: htmlContent }}
                    />
                </div>

                <div className="job-modal__footer">
                    {canApply ? (
                        <div className="job-modal__apply-buttons">
                            {/* E-Mail Bewerbung */}
                            <Link
                                href={`mailto:info@simplify-data.de?subject=${emailSubject}`}
                                className="job-modal__apply-btn"
                            >
                                <BiEnvelope /> {t('career_jobs_applyByEmail')}
                            </Link>
                            
                            {/* Arbeitsagentur Link */}
                            {hasArbeitsagenturLink && (
                                <Link
                                    href={job.arbeitsagentur_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="job-modal__apply-btn job-modal__apply-btn--secondary"
                                >
                                    <BiLinkExternal /> {t('career_jobs_applyArbeitsagentur')}
                                </Link>
                            )}
                        </div>
                    ) : (
                        <button disabled className="job-modal__apply-btn job-modal__apply-btn--disabled">
                            {t('career_jobs_noPositionsAvailable')}
                        </button>
                    )}
                    <button className="job-modal__close-btn" onClick={onClose}>
                        {t('career_jobs_close') || 'Schließen'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default JobDetailModal;
